import { authFetch, authText } from './auth-fetch';
import type { Page } from './endpoints';
import type { components } from './schema';

/**
 * The execution half of the API (`12 §8`) and the live stream in front of it
 * (`12 §14`).
 *
 * Same rule as the other two files: no client-side modelling and no renaming.
 * The one addition is {@link StreamEvent}, which is not an endpoint — it is the
 * shape Mercure delivers, and it is declared here because it has to stay in step
 * with `RunStreamPublisher` exactly as the response types stay in step with the
 * views.
 */

/**
 * **Taken from the generated schema, not written out here.**
 *
 * The first draft of this file spelled the step types by hand and invented one
 * called `message`. It compiled, matched nothing at runtime, and made the live
 * viewer report zero turns while three tools were running. Nothing caught it,
 * because the published contract had `"enum": "App\\Domain\\Task\\StepType"` —
 * a class name where a list of values belongs — and `openapi-typescript` widened
 * the property to `string`. Both halves are fixed: the document publishes the
 * values (guarded by `OpenApiDocumentTest`), and these aliases read them, so a
 * member renamed in PHP stops the frontend build.
 */
export type StepType = components['schemas']['RunStepSchema']['type'];
export type RunStatus = components['schemas']['RunSchema']['status'];

export interface RunSummary {
  id: string;
  task_id: string;
  employee_id: string;
  phase: string;
  status: RunStatus;
  attempt: number;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  model: string;
  last_step_seq: number;
  heartbeat_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  cost_micro_usd: number;
}

export interface RunDetail extends RunSummary {
  company_id: string;
  profile_version_id: string;
  profile_version: number;
  exit_reason: string | null;
  error: string | null;
  input_summary: string | null;
  output_summary: string | null;
  tokens: { in: number; out: number; cache_read: number; cache_write: number };
  turns: number;
  tool_calls: number;
  git: {
    base_sha: string | null;
    head_sha: string | null;
    files_changed: number | null;
    lines_added: number | null;
    lines_removed: number | null;
  };
  sandbox: {
    status: string;
    image: string;
    cpu_limit: string;
    memory_limit_mb: number;
    created_at: string | null;
    destroyed_at: string | null;
  } | null;
  created_at: string;
}

export interface RunStep {
  id: string;
  seq: number;
  type: StepType;
  tool_name: string | null;
  input: Record<string, unknown> | null;
  output_excerpt: string | null;
  is_error: boolean;
  tokens_in: number | null;
  tokens_out: number | null;
  duration_ms: number | null;
  created_at: string;
}

export interface RunArtifact {
  id: string;
  task_run_id: string;
  type: string;
  name: string;
  content_type: string;
  storage: 'inline' | 'filesystem';
  size_bytes: number;
  checksum: string;
  created_at: string;
}

export interface RealtimeGrant {
  hub_url: string;
  topics: string[];
  expires_in: number;
}

/**
 * What arrives on a run's topic.
 *
 * Two shapes on one channel, discriminated by `type` — the same two
 * `RunStreamPublisher` sends. A `run_status` is how a viewer learns the run
 * ended without polling for it, which matters most for the case it cannot
 * predict: **a run that finishes in the two seconds after Pause was pressed
 * comes back `succeeded`, not `paused`** (debt D4-R). The UI has to draw what
 * arrived rather than wait for the state it asked for.
 */
export type StreamEvent =
  | { type: 'steps'; task_run_id: string; steps: RunStep[] }
  | {
      type: 'run_status';
      task_run_id: string;
      task_id: string;
      status: RunStatus;
      phase: string;
      exit_reason: string | null;
      turns: number;
      tool_calls: number;
      cost_micro_usd: number;
      duration_ms: number | null;
    };

export interface StepFilters {
  type?: StepType;
  tool_name?: string;
  is_error?: boolean;
  after?: string;
  limit?: number;
}

export const runs = {
  active: (companyId: string) => authFetch<{ data: RunSummary[] }>(`/companies/${companyId}/runs/active`),
  get: (runId: string) => authFetch<RunDetail>(`/runs/${runId}`),
  steps: (runId: string, filters: StepFilters = {}) => {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }

    return authFetch<Page<RunStep>>(`/runs/${runId}/steps?${params.toString()}`);
  },
  artifacts: (runId: string) => authFetch<{ data: RunArtifact[] }>(`/runs/${runId}/artifacts`),
  /**
   * The bytes of one artifact, as text.
   *
   * Fetched rather than linked because the endpoint wants a bearer token and an
   * `<a href>` cannot carry one. Only worth calling for the text kinds — the
   * minutes of a meeting, a report, a diff — which is the caller's judgement to
   * make from `content_type`.
   */
  artifactContent: (artifactId: string) => authText(`/artifacts/${artifactId}/content`),

  /**
   * The three controls of `10 §2.5`.
   *
   * Live pause/cancel requests answer 202 with the current state. Cancelling
   * a queued or paused run answers 200 with the settled run: no runtime is
   * listening. Use the returned status, never assume the action has completed.
   */
  pause: (runId: string) => authFetch<RunDetail>(`/runs/${runId}/pause`, { method: 'POST' }),
  cancel: (runId: string) => authFetch<RunDetail>(`/runs/${runId}/cancel`, { method: 'POST' }),
  /** 201 with a **new** run: resuming continues the session in a fresh row. */
  resume: (runId: string) => authFetch<RunDetail>(`/runs/${runId}/resume`, { method: 'POST' }),
};

/**
 * `GET /realtime/token` (`12 §14`).
 *
 * The interesting part is invisible here: the response sets the
 * `mercureAuthorization` cookie, which is the only credential `EventSource` can
 * present. What comes back in the body is where to connect and for how long.
 */
export const realtime = {
  grant: () => authFetch<RealtimeGrant>('/realtime/token'),
};

/** `/companies/{c}/tasks/{t}/runs/{r}/steps` — the topic of `10 §6`. */
export function runTopic(companyId: string, taskId: string, runId: string): string {
  return `/companies/${companyId}/tasks/${taskId}/runs/${runId}/steps`;
}

export function activeRunsTopic(companyId: string): string {
  return `/companies/${companyId}/runs/active`;
}
